import { TablePaginationConfig } from "antd/es/table";
import { FilterValue, SorterResult } from "antd/es/table/interface";
import { MediaProjectListItem } from "pages/media/types";
import { useEffect, useMemo } from "react";

export const projectSorters: Record<
  string,
  (a: MediaProjectListItem, b: MediaProjectListItem) => number
> = {
  title: (a, b) => (a.title ?? "").localeCompare(b.title ?? ""),
  created: (a, b) => (a.created ?? "").localeCompare(b.created ?? ""),
  status: (a, b) => (a.status ?? "").localeCompare(b.status ?? ""),
};

export const createHandleTableChange = (
  setProjectsState: (state: {
    pagination: TablePaginationConfig;
    filters: Record<string, FilterValue | null>;
    sorter: any;
  }) => void
) => {
  return (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter:
      | SorterResult<MediaProjectListItem>
      | SorterResult<MediaProjectListItem>[]
  ) => {
    // Extract only serializable parts of sorter (Immer can't handle column with React components)
    const cleanSorter = Array.isArray(sorter)
      ? sorter.map(({ field, order }) => ({ field, order }))
      : { field: sorter.field, order: sorter.order };
    setProjectsState({
      pagination,
      filters,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      sorter: cleanSorter as any,
    });
  };
};

export const createGetFieldFilters = <
  T extends MediaProjectListItem,
  D extends readonly T[] | T[] | undefined,
>(
  data: D
) => {
  return (
    field: keyof T,
    callback: (id: string | undefined) => string = (id) => id ?? ""
  ) => {
    const values = data?.map((x) => x[field]);
    const unique = new Set(values);
    return [...unique]
      .sort((a, b) => {
        return (a ?? "")?.localeCompare(b ?? "");
      })
      .map((x) => ({
        text: callback(x as string),
        value: x as string,
      }));
  };
};

export const createHandleTableSearch = (
  setProjectsState: (state: { searchText: string }) => void
) => {
  return (query: string) => {
    setProjectsState({ searchText: query });
  };
};

export const useResetPaginationOnPageSizeChange = <
  TState extends {
    pagination: TablePaginationConfig;
    filters: Record<string, FilterValue | null>;
  },
>(
  setProjectsState: (state: Partial<TState>) => void,
  projectsState: TState,
  pagination: TablePaginationConfig
) => {
  useEffect(() => {
    setProjectsState({
      ...projectsState,
      pagination: {
        ...pagination,
        current: 1,
      },
    } as Partial<TState>);
  }, [pagination.pageSize, setProjectsState]);
};

export const useFilterProjectsBySearch = (
  data: MediaProjectListItem[] | readonly MediaProjectListItem[] | undefined,
  searchText: string
) => {
  return useMemo(
    () =>
      data?.filter((item) => {
        if (searchText === "") {
          return true;
        }
        const searchLower = searchText.toLowerCase();
        return (
          item.title.toLowerCase().includes(searchLower) ||
          item.id.toLowerCase().includes(searchLower) ||
          item.tags?.some((tag) =>
            tag.tag_value.toLowerCase().includes(searchLower)
          )
        );
      }),
    [data, searchText]
  );
};

export const centeredPagination = (
  pagination: false | TablePaginationConfig | undefined
): false | TablePaginationConfig | undefined =>
  pagination && {
    ...pagination,
    position: ["bottomCenter"],
    showSizeChanger: false,
  };

export const getTablePagination = (
  showPagination: boolean,
  pageSize: number,
  pagination: TablePaginationConfig
): false | TablePaginationConfig => {
  return showPagination
    ? {
        ...pagination,
        pageSize,
      }
    : false;
};
